import React from 'react';
import { StandardTableActions, Badge, statusVariantFromBoolean } from '../../components/ui';

import { Column } from '../../components/DataTable/DataTable';
import { TruncatedText } from '../../components/DataTable/TruncatedText';
import { Payee } from './useData';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { FormLayout } from '../../components/generic/FormLayoutRenderer';
import { APP_CONFIG } from '../../config/appConfig';

export const moduleConfig = {
  module: {
    title: 'Payees',
    description: 'Manage payees (suppliers, rent, utilities, etc.)',
    permissionModuleKey: 'expense_categories',
    route: {
      list: '/payees',
      new: '/payees/new',
      detail: (id: number) => `/payees/${id}`,
      edit: (id: number) => `/payees/${id}/edit`,
    },
    api: {
      list: '/payees',
      detail: (id: number) => `/payees/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: {
    placeholder: 'Search payees...',
  },
  filterGroups: [
    { name: 'basic', label: 'Basic', icon: '🔍', fields: ['name', 'defaultCategoryId', 'phone', 'address', 'vatNumber', 'isActive'] },
  ],
  filters: {
    fields: [
      {
        key: 'name',
        label: 'Name',
        placeholder: 'Search name...',
        type: 'text' as const,
        getValue: (item: Payee) => item.name,
        options: [],
      },
      {
        key: 'defaultCategoryId',
        label: 'Category',
        placeholder: 'All categories',
        type: 'select' as const,
        getValue: (item: Payee) => item.defaultCategory?.name ?? (item.defaultCategoryId != null ? `#${item.defaultCategoryId}` : ''),
        options: [],
        apiEndpoint: '/crm-expense-category',
        labelField: 'name',
      },
      {
        key: 'phone',
        label: 'Phone',
        placeholder: 'Search phone...',
        type: 'text' as const,
        getValue: (item: Payee) => item.phone ?? '',
        options: [],
      },
      {
        key: 'address',
        label: 'Address',
        placeholder: 'Search address...',
        type: 'text' as const,
        getValue: (item: Payee) => item.address ?? '',
        options: [],
      },
      {
        key: 'vatNumber',
        label: 'Identification',
        placeholder: 'VAT / Tax ID...',
        type: 'text' as const,
        getValue: (item: Payee) => item.vatNumber ?? '',
        options: [],
      },
      {
        key: 'isActive',
        label: 'Active',
        placeholder: 'All',
        type: 'boolean' as const,
        getValue: (item: Payee) => (item.isActive ? 'Active' : 'Inactive'),
        options: ['Active', 'Inactive'],
      },
    ],
    applyFilter: (items: Payee[], filters: Record<string, string[] | string>) => {
      return items.filter((item) => {
        const nameFilter = typeof filters.name === 'string' ? filters.name : Array.isArray(filters.name) ? (filters.name as string[])[0] : '';
        if (nameFilter && String(nameFilter).trim()) {
          const value = (item.name || '').toLowerCase();
          if (!value.includes(String(nameFilter).trim().toLowerCase())) return false;
        }

        const catArr = Array.isArray(filters.defaultCategoryId) ? filters.defaultCategoryId : filters.defaultCategoryId ? [filters.defaultCategoryId] : [];
        if (catArr.length > 0 && catArr.some((v) => (v || '').trim())) {
          const displayName = item.defaultCategory?.name ?? (item.defaultCategoryId != null ? `#${item.defaultCategoryId}` : '');
          const match = catArr.some((f) => {
            const v = String(f).trim();
            return displayName === v || (item.defaultCategoryId != null && v === `#${item.defaultCategoryId}`);
          });
          if (!match) return false;
        }

        const phoneFilter = typeof filters.phone === 'string' ? filters.phone : Array.isArray(filters.phone) ? (filters.phone as string[])[0] : '';
        if (phoneFilter && String(phoneFilter).trim()) {
          const value = (item.phone || '').toLowerCase();
          if (!value.includes(String(phoneFilter).trim().toLowerCase())) return false;
        }

        const addressFilter = typeof filters.address === 'string' ? filters.address : Array.isArray(filters.address) ? (filters.address as string[])[0] : '';
        if (addressFilter && String(addressFilter).trim()) {
          const value = (item.address || '').toLowerCase();
          if (!value.includes(String(addressFilter).trim().toLowerCase())) return false;
        }

        const vatFilter = typeof filters.vatNumber === 'string' ? filters.vatNumber : Array.isArray(filters.vatNumber) ? (filters.vatNumber as string[])[0] : '';
        if (vatFilter && String(vatFilter).trim()) {
          const value = (item.vatNumber || '').toLowerCase();
          if (!value.includes(String(vatFilter).trim().toLowerCase())) return false;
        }

        if (filters.isActive && (Array.isArray(filters.isActive) ? filters.isActive : [filters.isActive]).some((v) => (v || '').trim())) {
          const status = item.isActive ? 'Active' : 'Inactive';
          const activeArr = Array.isArray(filters.isActive) ? filters.isActive : [filters.isActive];
          if (!activeArr.includes(status)) return false;
        }

        return true;
      });
    },
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'Add Payee',
        showTitle: false,
        variant: 'primary' as const,
        href: '/payees/new',
      },
    ],
  },
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: Payee) => { window.location.href = `/payees/${item.id}`; },
    keyExtractor: (item: Payee) => item.id,
  },
  emptyStates: {
    noData: 'Get started by creating a payee.',
    noSearchResults: 'No payees found matching your search.',
    noFilterResults: 'No payees match the selected filters.',
  },
  delete: {
    title: 'Delete Payee',
    message: 'Are you sure you want to delete this payee?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete payee.',
    handler: async (id: number) => {
      const { deletePayee } = await import('../../services/mutations');
      await deletePayee(id);
    },
  },
  utils: {
    formatDate: (dateString?: string) => (dateString ? new Date(dateString).toLocaleDateString() : '-'),
  },
  detail: {
    subtitle: 'Payee Details',
    notFoundTitle: 'Payee not found',
    notFoundMessage: 'This payee does not exist.',
    notFoundBackText: 'Back to Payees',
    defaultTitle: 'Unnamed Payee',
    getTitle: (item: Payee) => item.name || `Payee #${item.id}`,
  },
  form: {
    new: { title: 'New Payee', subtitle: 'Add a payee', submitText: 'Create Payee' },
    edit: { title: 'Edit Payee', subtitle: 'Update payee', submitText: 'Update Payee' },
    cancelText: 'Cancel',
    loadingText: 'Loading payee...',
    errorMessage: 'Failed to save payee.',
    create: async (data: Partial<Payee> & { defaultCategoryId?: string | number; defaultSubcategoryId?: string | number }) => {
      const { createPayee } = await import('../../services/mutations');
      const payload = {
        name: data.name ?? '',
        notes: data.notes,
        email: data.email,
        phone: data.phone,
        address: data.address,
        vatNumber: data.vatNumber,
        isActive: data.isActive,
        defaultCategoryId: data.defaultCategoryId != null && String(data.defaultCategoryId) !== '' ? Number(data.defaultCategoryId) : null,
        defaultSubcategoryId: data.defaultSubcategoryId != null && String(data.defaultSubcategoryId) !== '' ? Number(data.defaultSubcategoryId) : null,
      };
      return await createPayee(payload);
    },
    update: async (id: number, data: Partial<Payee> & { defaultCategoryId?: string | number; defaultSubcategoryId?: string | number }) => {
      const { updatePayee } = await import('../../services/mutations');
      const payload = {
        ...data,
        defaultCategoryId: data.defaultCategoryId != null && String(data.defaultCategoryId) !== '' ? Number(data.defaultCategoryId) : null,
        defaultSubcategoryId: data.defaultSubcategoryId != null && String(data.defaultSubcategoryId) !== '' ? Number(data.defaultSubcategoryId) : null,
      };
      return await updatePayee(id, payload);
    },
  },
  fields: [
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      placeholder: 'e.g. Landlord, Electric Co.',
      validation: { required: true, minLength: 1, maxLength: 255 },
    },
    { name: 'email', label: 'Email', type: 'text', placeholder: 'Email' },
    { name: 'phone', label: 'Phone', type: 'text', placeholder: 'Phone' },
    { name: 'address', label: 'Address', type: 'text', placeholder: 'Address' },
    { name: 'vatNumber', label: 'VAT / Tax ID', type: 'text', placeholder: 'VAT number' },
    {
      name: 'defaultCategoryId',
      label: 'Default expense category',
      type: 'select',
      placeholder: 'Optional',
      options: [{ value: '', label: 'None' }],
      apiEndpoint: '/crm-expense-category',
      labelField: 'name',
      valueField: 'id' as const,
    },
    {
      name: 'defaultSubcategoryId',
      label: 'Default expense subcategory',
      type: 'select',
      placeholder: 'Select category first',
      options: [{ value: '', label: 'None' }],
      optionsEndpointFromFormData: (formData) => {
        const id = formData.defaultCategoryId;
        if (id == null || id === '') return null;
        return `/payees/subcategories/${Number(id)}`;
      },
      optionsDependOnFields: ['defaultCategoryId'],
      labelField: 'name',
      valueField: 'id' as const,
    },
    { name: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Notes', rows: 2 },
    { name: 'isActive', label: 'Active', type: 'checkbox' },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default' as const,
    sections: [
      {
        id: 'basic',
        title: 'Payee Information',
        icon: '📝',
        fields: ['name', 'isActive', 'email', 'phone', 'vatNumber', 'address', 'defaultCategoryId', 'defaultSubcategoryId', 'notes'],
        gridCols: 'md:grid-cols-12',
        collapsible: false,
        fieldSpans: {
          name: 'md:col-span-10',
          isActive: 'md:col-span-2',
          email: 'md:col-span-4',
          phone: 'md:col-span-4',
          vatNumber: 'md:col-span-4',
          address: 'md:col-span-12',
          defaultCategoryId: 'md:col-span-6',
          defaultSubcategoryId: 'md:col-span-6',
          notes: 'md:col-span-12',
        },
      },
    ],
  } as FormLayout,
};

export const getColumns = (_formatDate: (dateString?: string) => string): Column<Payee>[] => [
  {
    key: 'name',
    header: 'Name',
    sortable: true,
    render: (value, row) => (
      <div className="min-w-0">
        <div>{value || '-'}</div>
        {row.email && (
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {row.email}
          </div>
        )}
      </div>
    ),
  },
  {
    key: 'defaultCategory',
    header: 'Category',
    sortable: true,
    render: (_, row) => (
      <div className="min-w-0">
        <div>{row.defaultCategory?.name || '-'}</div>
        {row.defaultSubcategory?.name && (
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate flex items-center gap-1">
            <svg className="w-3.5 h-3.5 shrink-0 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            {row.defaultSubcategory.name}
          </div>
        )}
      </div>
    ),
  },
  { key: 'phone', header: 'Phone', sortable: true, render: (value) => value || '-' },
  {
    key: 'address',
    header: 'Address',
    sortable: true,
    render: (value) => value ? <TruncatedText text={value} maxLength={40} showTooltip /> : '-',
  },
  { key: 'vatNumber', header: 'Identification', sortable: true, render: (value) => value || '-' },
  {
    key: 'isActive',
    header: 'Active',
    sortable: true,
    render: (_, row) => (
      <Badge variant={statusVariantFromBoolean(!!row.isActive)}>
        {row.isActive ? 'Active' : 'Inactive'}
      </Badge>
    ),
  },
];

export const getDetailSections = (item: Payee, _formatDate: (dateString?: string) => string) => [
  {
    title: 'Basic Information',
    icon: '📝',
    fields: [
      { label: 'Name', value: item.name || '-' },
      { label: 'Email', value: item.email || '-' },
      { label: 'Phone', value: item.phone || '-' },
      { label: 'Address', value: item.address || '-' },
      { label: 'VAT / Tax ID', value: item.vatNumber || '-' },
      { label: 'Default category', value: item.defaultCategory?.name || '-' },
      { label: 'Default subcategory', value: item.defaultSubcategory?.name || '-' },
      { label: 'Active', value: item.isActive ? 'Yes' : 'No' },
    ],
  },
  ...(item.notes ? [{ title: 'Notes', icon: '📋' as const, fields: [{ label: 'Notes', value: item.notes }] }] : []),
];

export const getStatusBadges = (_item: Payee) => null;

export type PayeeFormData = Partial<Omit<Payee, 'defaultCategoryId' | 'defaultSubcategoryId'>> & { defaultCategoryId?: string; defaultSubcategoryId?: string };
export const getInitialFormData = (): PayeeFormData => ({
  name: '',
  email: '',
  phone: '',
  address: '',
  vatNumber: '',
  defaultCategoryId: '',
  defaultSubcategoryId: '',
  notes: '',
  isActive: true,
});
export const mapItemToFormData = (item: Payee): PayeeFormData => ({
  name: item.name || '',
  email: item.email || '',
  phone: item.phone || '',
  address: item.address || '',
  vatNumber: item.vatNumber || '',
  defaultCategoryId: item.defaultCategoryId != null ? String(item.defaultCategoryId) : '',
  defaultSubcategoryId: item.defaultSubcategoryId != null ? String(item.defaultSubcategoryId) : '',
  notes: item.notes || '',
  isActive: item.isActive,
});

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  }
  return null;
};

export const getTableActions = (
  item: Payee,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};

